// frontend/src/components/editor/hooks/useStreamingGenerate.ts
import { useState, useCallback, useRef } from 'react';
import { ContentBlock, OutlineItem, HistoricalChapterRef } from '../../../types';
import { toast } from '../../../store/toastStore';
import { contentToBlocks } from '../../../utils/contentBlocks';

/** Deep-update an outline item by uid, applying fn to the matched node */
function updateOutlineItem(
  items: OutlineItem[],
  uid: string,
  fn: (item: OutlineItem) => OutlineItem
): OutlineItem[] {
  return items.map((item) => {
    if (item.uid === uid) return fn(item);
    if (item.children?.length) {
      return { ...item, children: updateOutlineItem(item.children, uid, fn) };
    }
    return item;
  });
}

/** Deep-map all items in outline tree */
function mapAllItems(
  items: OutlineItem[],
  fn: (item: OutlineItem) => OutlineItem
): OutlineItem[] {
  return items.map((item) => {
    const updated = fn(item);
    if (updated.children?.length) {
      return { ...updated, children: mapAllItems(updated.children, fn) };
    }
    return updated;
  });
}

interface UseStreamingGenerateOptions {
  projectId: string;
  setOutline: React.Dispatch<React.SetStateAction<OutlineItem[]>>;
  modelConfigId: number | null;
  useRAG?: boolean;
  selectedSkillId?: number | null;
  imageMode?: 'none' | 'mermaid';
  diagramStyle?: string;  // 'default' | 'random' | '<image-skill id>'
  onSearchRefs?: (refs: { title: string; url: string }[]) => void;
  historicalChapterRefs?: HistoricalChapterRef[] | null;
}

let _blockCounter = 0;
// 完整的内联图片标记（用于"不生图"模式剥离残留标记）
const _IMG_MARKER_RE = /<!--\s*img:(?:search|generate):[\s\S]*?-->/g;
// 剥离模型思考链 <think>...</think>，含流式中尚未闭合的尾部，避免思考内容被显示/保存
function stripThinking(s: string): string {
  return s.replace(/<think>[\s\S]*?<\/think>/g, '').replace(/<think>[\s\S]*$/g, '');
}
function nextBlockId(): string {
  _blockCounter += 1;
  return `${Date.now().toString(36)}-${_blockCounter.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function useStreamingGenerate({
  projectId,
  setOutline,
  modelConfigId,
  useRAG = false,
  selectedSkillId = null,
  imageMode = 'mermaid',
  diagramStyle = 'default',
  onSearchRefs,
  historicalChapterRefs,
}: UseStreamingGenerateOptions) {
  const [isStreaming, setIsStreaming] = useState(false);
  // Track historical refs for regenerations
  const historicalRefsRef = useRef<HistoricalChapterRef[] | null>(historicalChapterRefs ?? null);
  historicalRefsRef.current = historicalChapterRefs ?? null;
  const [currentChapterUid, setCurrentChapterUid] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  // Track current generation to prevent stale finally blocks from corrupting state
  const generationIdRef = useRef(0);
  // Auto-incrementing figure number for image captions
  const figureCounterRef = useRef(1);
  // Chapter ID prefix for figure numbering (e.g., "1", "1.1")
  const chapterPrefixRef = useRef('');
  // Track already-resolved image queries to prevent duplicate captions
  const resolvedImageQueries = useRef(new Set<string>()).current;

  const generateChapter = useCallback(
    async (item: OutlineItem, opts?: { customPrompt?: string; preserveExisting?: boolean }) => {
      if (!modelConfigId) {
        toast.error('请先在「模型设置」中配置 AI 模型');
        return;
      }

      const API_BASE = process.env.REACT_APP_API_URL ?? '';
      const token = localStorage.getItem('token');
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      // Bump generation id to invalidate stale finally blocks
      generationIdRef.current += 1;
      const myGenId = generationIdRef.current;
      // Reset figure counter and resolved images for new chapter
      figureCounterRef.current = 1;
      resolvedImageQueries.clear();
      // Set chapter prefix for figure numbering (e.g., "1" → "图 1-1")
      chapterPrefixRef.current = item.id || '';

      setIsStreaming(true);
      setCurrentChapterUid(item.uid);

      // 初始化空块数组
      const initialBlocks: ContentBlock[] = [
        {
          id: nextBlockId(),
          type: 'heading',
          content: `<h2>${item.id} ${item.title}</h2>`,
          meta: { level: 2 },
        },
      ];

      setOutline((prev) =>
        updateOutlineItem(prev, item.uid, (o) => ({ ...o, content: initialBlocks, generating: true }))
      );

      try {
        const resp = await fetch(
          `${API_BASE}/api/projects/${projectId}/${
            useRAG ? 'generate/content-rag' : 'generate/content'
          }`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              item_id: item.id,
              item_uid: item.uid,
              chapter: { ...item, tag: item.tag || '' },
              model_config_id: modelConfigId,
              use_web_search: useRAG,
              image_mode: imageMode,
              diagram_style: imageMode === 'none' ? 'default' : diagramStyle,
              ...(selectedSkillId ? { skill_id: selectedSkillId } : {}),
              ...(historicalRefsRef.current && historicalRefsRef.current.length > 0
                ? { historical_chapter_refs: historicalRefsRef.current, use_historical_data: true }
                : {}),
              ...(opts?.customPrompt ? { custom_prompt: opts.customPrompt } : {}),
              ...(opts?.preserveExisting === false ? { clear_existing: true } : {}),
            }),
            signal: abortRef.current.signal,
          }
        );

        if (!resp.ok) throw new Error(await resp.text());

        const reader = resp.body?.getReader();
        if (!reader) throw new Error('无法读取响应流');
        readerRef.current = reader;

        const decoder = new TextDecoder();
        let rawContent = '';
        let buf = '';
        // Throttle: only update React state every ~200ms
        let lastUpdateTime = 0;

        while (true) {
          if (generationIdRef.current !== myGenId) return; // Stale generation, exit cleanly

          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const lines = buf.split('\n');
          buf = lines.pop() || '';
          for (const line of lines) {
            const t = line.trim();
            if (!t || !t.startsWith('data: ')) continue;
            const json = t.slice(6);
            if (json === '[DONE]') continue;
            try {
              const p = JSON.parse(json);
              if (p.chunk || p.type === 'content_chunk') {
                rawContent += p.chunk || '';
              } else if (p.type === 'image_placeholder') {
                // Marker already in rawContent from chunk stream
              } else if (p.type === 'image_resolved') {
                // Skip if this image query was already resolved (prevents duplicate captions)
                if (resolvedImageQueries.has(p.query)) continue;
                resolvedImageQueries.add(p.query);

                // Replace marker with <img> + numbered caption using TipTap-compatible <p> wrappers
                const escapedQuery = p.query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const markerRegex = new RegExp(
                  `<!--\\s*img:[^:]*:${escapedQuery}\\s*-->`,
                );
                const imgUrl = p.file_path
                  ? (p.file_path.startsWith('http') ? p.file_path : `${API_BASE}/${p.file_path.replace(/^\//, '')}`)
                  : '';
                if (imgUrl) {
                  const figNum = figureCounterRef.current++;
                  const prefix = chapterPrefixRef.current;
                  const baseNum = prefix ? `图 ${prefix}-${figNum}` : `图 ${figNum}`;
                  // 图注显示"图号 + 图片描述"（query 即图片描述）
                  const safeQuery = p.query.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
                  const label = `${baseNum} ${safeQuery}`;
                  const figureHtml = `<p style="text-align:center;margin:16px 0 4px 0;text-indent:0 !important;">
  <img src="${imgUrl}" alt="${label}" style="max-width:100%;height:auto;" />
</p>
<p style="text-align:center;font-size:10pt;color:#555;margin:0 0 16px 0;text-indent:0 !important;">${label}</p>`;
                  rawContent = rawContent.replace(markerRegex, figureHtml);
                } else {
                  rawContent = rawContent.replace(markerRegex, `<!-- unresolved:${p.query} -->`);
                }
              } else if (p.type === 'search_results') {
                const refs = (p.results || []).slice(0, 10);
                onSearchRefs?.(refs);
              } else if (p.error) {
                throw new Error(p.message || '未知错误');
              }
            } catch (e: any) {
              if (e.message && !e.message.includes('Unexpected token')) throw e;
            }
          }

          // Throttled re-parse — re-parse ALL content each time for correct streaming display
          const now = Date.now();
          if (now - lastUpdateTime > 200 && rawContent.length > 0) {
            lastUpdateTime = now;
            // 不生图模式：剥离残留的图片标记，避免出现"未匹配"占位
            const parseSrc = stripThinking(imageMode === 'none' ? rawContent.replace(_IMG_MARKER_RE, '') : rawContent);
            const allBlocks = contentToBlocks(parseSrc);

            const currentBlocks: ContentBlock[] = [
              initialBlocks[0],
              ...allBlocks,
            ];
            setOutline((prev) =>
              updateOutlineItem(prev, item.uid, (o) => ({ ...o, content: currentBlocks, generating: true }))
            );
          }
        }

        // Final — re-parse everything from scratch for correct paragraph boundaries
        const finalSrc = stripThinking(imageMode === 'none' ? rawContent.replace(_IMG_MARKER_RE, '') : rawContent);
        const allBlocks = contentToBlocks(finalSrc);

        const finalBlocks: ContentBlock[] = [
          initialBlocks[0],
          ...allBlocks,
        ];
        setOutline((prev) =>
          updateOutlineItem(prev, item.uid, (o) => ({ ...o, content: finalBlocks, generating: false }))
        );
        toast.success(`${item.id} ${item.title} 生成完成`);
      } catch (e: any) {
        if (e.name === 'AbortError' || generationIdRef.current !== myGenId) return;
        // Only show error for the active generation
        if (generationIdRef.current === myGenId) {
          toast.error(`生成失败: ${e.message}`);
        }
        setOutline((prev) =>
          updateOutlineItem(prev, item.uid, (o) => ({ ...o, generating: false }))
        );
      } finally {
        readerRef.current = null;
        // Only clear streaming state if THIS generation is still the active one
        if (generationIdRef.current === myGenId) {
          setIsStreaming(false);
          setCurrentChapterUid(null);
        }
      }
    },
    [projectId, setOutline, modelConfigId, useRAG, selectedSkillId, imageMode, diagramStyle, onSearchRefs]
  );

  const cancelGeneration = useCallback(() => {
    // Cancel the stream reader first to stop the while(true) loop immediately
    readerRef.current?.cancel().catch(() => {});
    readerRef.current = null;
    abortRef.current?.abort();
    generationIdRef.current += 1; // Invalidate current generation
    setIsStreaming(false);
    setCurrentChapterUid(null);
    // Clear generating flag on any chapter that was being generated
    setOutline((prev) =>
      mapAllItems(prev, (o) => (o.generating ? { ...o, generating: false } : o))
    );
  }, [setOutline]);

  return {
    generateChapter,
    cancelGeneration,
    isStreaming,
    currentChapterUid,
  };
}
